import { Box, Grid } from '@mui/material';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectPEPQuestions, selectUser } from '@/redux/user-data/selectors';

import { PEPAnswer, PEPQuestion, QuestionCode } from '@/types/api/pep';

import { formatDate, renderHTMLText } from '@/utils/string';

import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

const FIELD_PREFIX = 'moreInfo.reviewMyInformation.governmentRoles.field';

export function GovernmentRolesSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectUser);
  const pepQuestions = useAppSelector(selectPEPQuestions);

  const questions = pepQuestions
    .filter(question =>
      [QuestionCode.Connection, QuestionCode.Relatives].includes(
        question.code as QuestionCode,
      ),
    )
    .sort((a, b) => a.priority - b.priority);

  const answerMap = Object.fromEntries(
    (user?.lastAnswer?.answers ?? []).map(a => [a.questionId, a]),
  );

  return (
    <ReviewCard
      title={t('moreInfo.reviewMyInformation.governmentRoles.title')}
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      {questions.map((question, index) => (
        <Box key={question.id}>
          {index > 0 && <Divider width={'100%'} sx={{ mt: 1.5 }} />}
          <PepAnswerReview
            question={question}
            answer={answerMap[question.id]}
          />
        </Box>
      ))}
    </ReviewCard>
  );
}

type PepAnswerReviewProps = {
  question: PEPQuestion;
  answer: PEPAnswer | undefined;
};

function PepAnswerReview({ question, answer }: Readonly<PepAnswerReviewProps>) {
  const { t } = useTranslate();
  const isPositive = answer?.positive ?? false;

  return (
    <Box>
      <InfoReviewFieldLabel
        component="div"
        sx={{
          'p, ul': {
            margin: 0,
          },
          ul: {
            pl: theme => theme.spacing(2.5),
          },
        }}
      >
        {renderHTMLText(question.question)}
        {question.additionalInfo && (
          <>
            <br />
            {renderHTMLText(question.additionalInfo)}
          </>
        )}
      </InfoReviewFieldLabel>
      <InfoReviewFieldValue>
        {isPositive ? t('common.yes') : t('common.no')}
      </InfoReviewFieldValue>
      {answer && isPositive && <PepYesAnswerDetails entry={answer} />}
    </Box>
  );
}

function PepYesAnswerDetails({ entry }: Readonly<{ entry: PEPAnswer }>) {
  const { t, country } = useTranslate();
  const { dateFormat } = useAppSelector(selectConfigsState);

  return (
    <>
      <Grid container columnSpacing={2}>
        {entry.fullName && (
          <Grid item xs={6}>
            <InfoReviewFieldLabel>
              {t(`${FIELD_PREFIX}.fullName`)}
            </InfoReviewFieldLabel>
            <InfoReviewFieldValue>{entry.fullName}</InfoReviewFieldValue>
          </Grid>
        )}
        {entry.relationship && (
          <Grid item xs={6}>
            <InfoReviewFieldLabel>
              {t(`${FIELD_PREFIX}.relationship`)}
            </InfoReviewFieldLabel>
            <InfoReviewFieldValue>{entry.relationship}</InfoReviewFieldValue>
          </Grid>
        )}
      </Grid>
      <Grid container columnSpacing={2}>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.position`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{entry.position}</InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.country`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {entry.countryCode &&
              (country[entry.countryCode] || entry.countryCode)}
          </InfoReviewFieldValue>
        </Grid>
        <Grid item xs={6}>
          <InfoReviewFieldLabel>
            {t(`${FIELD_PREFIX}.startDate`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {formatDate(entry.startDate ?? '', dateFormat)}
          </InfoReviewFieldValue>
        </Grid>
        {entry.endDate && (
          <Grid item xs={6}>
            <InfoReviewFieldLabel>
              {t(`${FIELD_PREFIX}.endDate`)}
            </InfoReviewFieldLabel>
            <InfoReviewFieldValue>
              {formatDate(entry.endDate, dateFormat)}
            </InfoReviewFieldValue>
          </Grid>
        )}
      </Grid>
    </>
  );
}
